/**
 * Tail colliders — ADR-038 §3.3: **fit the pelvis sphere and the two thigh
 * capsules a tail spring collides with, from this character's own flesh, and
 * never start a tail inside one.**
 *
 * ONE responsibility: measure, at rest, the colliders that keep a tail out of
 * the legs. A fixed table of body spheres (World's `BODY_COLLIDERS`, keyed to
 * VRoid's `J_Bip_*` names) fails on exactly the generated bodies Harry
 * normalises — so the sizes come from the mesh:
 *
 *   - THIGH CAPSULE, hip joint → knee, radius = the median distance to that
 *     segment of the vertices the thigh (or a bone between it and the knee)
 *     dominates, × 0.9 (a capsule inside the flesh, not around it);
 *   - PELVIS SPHERE near the hips (slid off the joint away from a tail rooted
 *     inside the flesh — see the pelvis block), radius = the median distance of the vertices
 *     the pelvis dominates — the hips bone and whatever hangs under it that is
 *     not a leg, the spine or the tail (ActorCore's `Hip → Pelvis → thighs`
 *     keeps its pelvis flesh one bone down) — × 0.9.
 *
 * Then every collider is CLIPPED so no tail joint, with its hit radius and a
 * 5 mm margin, starts inside it (a spring that begins in penetration is shoved
 * out on its first step — the rest pose would jump). A clip is reported, never
 * silent: the report says which collider shrank, from what, and for which
 * joint. A collider clipped below 1 cm is dropped and reported.
 *
 * Offsets are in the collider bone's LOCAL frame (the spec): the capsule's
 * head sits on the thigh joint (offset 0) and its tail is the knee expressed
 * in the thigh's frame.
 *
 * Depends on: three, kernel (types). Depended on by: app (the tail spring's
 * defaults), the spring probe.
 */
import { Vector3, type Object3D, type SkinnedMesh } from 'three';
import type { SpringCollider } from '@harryhausen/kernel';

export interface TailColliderInput {
  readonly root: Object3D;
  readonly mesh: SkinnedMesh;
  /** Bone names (the scene's) for the pelvis and each leg's thigh and knee. */
  readonly hips: string;
  /** The first spine bone — its subtree is not pelvis flesh. */
  readonly spine?: string;
  /** The tail's own bones — their subtree is not pelvis flesh. */
  readonly tailRoot?: string;
  readonly legs: readonly { readonly thigh: string; readonly knee: string }[];
  /** The tail's joint positions at rest (world) that collide, with their hit radii. */
  readonly tailPoints: readonly { readonly position: Vector3; readonly hitRadius: number }[];
  /** The names the FILE speaks for the collider bones (`_graft` stripped); defaults to the scene names. */
  readonly fileName?: (sceneName: string) => string;
}

export interface TailColliderReport {
  readonly colliders: readonly SpringCollider[];
  readonly fitted: readonly {
    readonly bone: string;
    readonly kind: 'sphere' | 'capsule';
    readonly fleshRadius: number;
    readonly radius: number;
    /** The tail joint that forced a clip, or null when the flesh radius stood. */
    readonly clippedBy: number | null;
    readonly dropped: boolean;
    /** How far the centre slid off its bone, away from the tail's root (m; spheres only). */
    readonly slide?: number;
  }[];
}

const MARGIN = 0.005;
const MIN_RADIUS = 0.01;
const FLESH_SHARE = 0.9;

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)]!;
}

function distanceToSegment(p: Vector3, a: Vector3, b: Vector3): number {
  const ab = b.clone().sub(a);
  const t = Math.min(1, Math.max(0, p.clone().sub(a).dot(ab) / Math.max(ab.lengthSq(), 1e-12)));
  return p.distanceTo(a.clone().addScaledVector(ab, t));
}

/** Fit the pelvis sphere and thigh capsules at rest. The scene must be at rest. */
export function fitTailColliders(input: TailColliderInput): TailColliderReport {
  const { root, mesh } = input;
  const fileName = input.fileName ?? ((n: string) => n);
  root.updateMatrixWorld(true);
  const node = (name: string): Object3D => {
    const n = root.getObjectByName(name);
    if (n === undefined) throw new Error(`fitTailColliders: no bone "${name}" in the scene`);
    return n;
  };
  // Rest-pose vertex positions (bind == rest for a freshly run character) and dominant bone.
  const pos = mesh.geometry.getAttribute('position');
  const si = mesh.geometry.getAttribute('skinIndex');
  const sw = mesh.geometry.getAttribute('skinWeight');
  const bones = mesh.skeleton.bones;
  const dominant: number[] = [];
  const world: Vector3[] = [];
  for (let v = 0; v < pos.count; v++) {
    let best = -1;
    let bw = 0;
    for (let k = 0; k < 4; k++) {
      const w = sw.getComponent(v, k);
      if (w > bw) {
        bw = w;
        best = si.getComponent(v, k);
      }
    }
    dominant.push(best);
    world.push(mesh.getVertexPosition(v, new Vector3()).applyMatrix4(mesh.matrixWorld));
  }
  const within = (bone: Object3D, stop: Object3D | readonly Object3D[] | null): Set<number> => {
    // Skeleton indices of `bone` and its descendants, not descending past `stop`.
    const stops = new Set(stop === null ? [] : Array.isArray(stop) ? stop : [stop as Object3D]);
    const out = new Set<number>();
    const walk = (o: Object3D): void => {
      if (stops.has(o)) return;
      const i = bones.indexOf(o as never);
      if (i >= 0) out.add(i);
      for (const c of o.children) walk(c);
    };
    walk(bone);
    return out;
  };

  const fitted: TailColliderReport['fitted'][number][] = [];
  const colliders: SpringCollider[] = [];
  const clip = (flesh: number, coreDistance: (p: Vector3) => number): { radius: number; clippedBy: number | null } => {
    let radius = flesh;
    let clippedBy: number | null = null;
    input.tailPoints.forEach((t, k) => {
      const room = coreDistance(t.position) - t.hitRadius - MARGIN;
      if (room < radius) {
        radius = room;
        clippedBy = k;
      }
    });
    return { radius, clippedBy };
  };

  // Pelvis sphere. A tail can root INSIDE the pelvis flesh (the kobold's did,
  // 4.5 cm from its hip joint, while the zone climbed the back — before #180's
  // 095ca79; now it roots 9 cm off and needs no slide), and a sphere centred
  // on the joint would then have to shrink to nothing to clear it. The centre is therefore slid AWAY from the tail's root along the
  // hip-from-tail direction, staying inside the flesh ball (radius ≤ flesh − the
  // slide), and the slide that leaves the largest clear sphere wins — the
  // pelvis keeps a collider that still blocks a tail swinging through the body.
  {
    const hips = node(input.hips);
    const centre = hips.getWorldPosition(new Vector3());
    const stops = [
      ...input.legs.map((l) => node(l.thigh)),
      ...(input.spine !== undefined ? [node(input.spine)] : []),
      ...(input.tailRoot !== undefined ? [node(input.tailRoot)] : []),
    ];
    const own = within(hips, stops);
    const d = world.filter((_, v) => own.has(dominant[v]!)).map((p) => p.distanceTo(centre));
    const flesh = median(d) * FLESH_SHARE;
    const root = input.tailPoints[0]?.position;
    const away = root === undefined ? new Vector3() : centre.clone().sub(root);
    if (away.lengthSq() > 1e-12) away.normalize();
    let best = { slide: 0, radius: -Infinity, clippedBy: null as number | null, at: centre.clone() };
    for (let i = 0; i <= 20; i++) {
      const slide = (flesh * i) / 20;
      const at = centre.clone().addScaledVector(away, slide);
      const fit = clip(flesh - slide, (p) => p.distanceTo(at));
      if (fit.radius > best.radius) best = { slide, radius: fit.radius, clippedBy: fit.clippedBy, at };
    }
    const dropped = best.radius < MIN_RADIUS;
    fitted.push({ bone: input.hips, kind: 'sphere', fleshRadius: flesh, radius: best.radius, clippedBy: best.clippedBy, dropped, slide: best.slide });
    if (!dropped) {
      const offset = hips.worldToLocal(best.at.clone());
      colliders.push({ bone: fileName(input.hips), shape: { sphere: { offset: [offset.x, offset.y, offset.z], radius: best.radius } } });
    }
  }

  // Thigh capsules.
  for (const leg of input.legs) {
    const thigh = node(leg.thigh);
    const knee = node(leg.knee);
    const a = thigh.getWorldPosition(new Vector3());
    const b = knee.getWorldPosition(new Vector3());
    const own = within(thigh, knee);
    const d = world.filter((_, v) => own.has(dominant[v]!)).map((p) => distanceToSegment(p, a, b));
    const flesh = median(d) * FLESH_SHARE;
    const { radius, clippedBy } = clip(flesh, (p) => distanceToSegment(p, a, b));
    const dropped = radius < MIN_RADIUS;
    fitted.push({ bone: leg.thigh, kind: 'capsule', fleshRadius: flesh, radius, clippedBy, dropped });
    if (!dropped) {
      const tail = thigh.worldToLocal(b.clone());
      colliders.push({ bone: fileName(leg.thigh), shape: { capsule: { offset: [0, 0, 0], radius, tail: [tail.x, tail.y, tail.z] } } });
    }
  }
  return { colliders, fitted };
}
